/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { Stack } from "@fluentui/react";

import * as distributionListsActions from "Dataflows/DistributionLists/DistributionListsAction";
import * as distributionListsSelectors from "Dataflows/DistributionLists/DistributionListsSelector";

import { useAuth } from "Authentication/AuthProvider";
import ListCounts from "Components/Common/ListCounts/ListCounts";
import { Title } from "Components/Common/Results/Result.styles";
import DistributionListPendingRequestsList from "Components/DistributionLists/DistributionListPendingRequestsList";
import DistributionListsJoinRequestForm from "Components/DistributionListsJoinRequestForm/DistributionListsJoinRequestForm";
import { defaultListingPageSize } from "Constants/AdminConstants";
import { OperationStatus } from "Constants/OperationStatus";
import { PermissionsEnum } from "Constants/RolesPermissionsConstants";
import { SubSiteUrlRootPath, TenantTypesEnum } from "Constants/TenantTypes";
import {
  containerStackStyles,
  listStackStyles,
  tableStackStyles,
  PaddedFlexRow,
} from "Containers/Common/CommonContainer.styles";
import { IDistributionListsJoinRequest } from "Interfaces/DistributionLists/IDistributionListsJoinRequest";

import { IDistributionListsContainerMatchProps } from "./IDistributionListRequestsContainerProps";

const DistributionListRequestsContainer: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const routeParams = useParams<IDistributionListsContainerMatchProps>();
  const tenant = SubSiteUrlRootPath[routeParams.tenant];

  const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(false);
  const [selectedJoinRequest, setSelectedJoinRequest] = useState<IDistributionListsJoinRequest>();

  const networkOperationStatus = useSelector(distributionListsSelectors.getNetworkOperationStatus);
  const networkOperationStatusApprove = useSelector(
    distributionListsSelectors.getNetworkOperationStatusApprove
  );
  const networkOperationStatusError = useSelector(
    distributionListsSelectors.getNetworkOperationStatusError
  );
  const networkOperationStatusApproveError = useSelector(
    distributionListsSelectors.getNetworkOperationStatusApproveError
  );
  const pageSize = useSelector(distributionListsSelectors.getPageSize);
  const currentPage = useSelector(distributionListsSelectors.getCurrentPage);
  const distributionLists = useSelector(distributionListsSelectors.getDistributionLists);
  const filteredCount = useSelector(distributionListsSelectors.getFilteredCount);
  const currentTenant = useSelector(distributionListsSelectors.getCurrentTenant);

  const searchDistributionLists = (tenant, page, pageSize): void => {
    dispatch(distributionListsActions.searchDistributionLists(tenant, page, pageSize));
  };

  const updateCurrentPage = (currentPage: number): void => {
    dispatch(distributionListsActions.updateCurrentPage(currentPage));
  };

  const updatePageSize = (pageSize: number): void => {
    dispatch(distributionListsActions.updatePageSize(pageSize));
  };

  const resetDistributionLists = (): void => {
    dispatch(distributionListsActions.resetDistributionLists());
  };

  const approveOrDeclineJoinRequest = (
    joinRequestId: number,
    tenant: string,
    approved: boolean
  ): void => {
    dispatch(distributionListsActions.approveOrDeclineJoinRequest(joinRequestId, tenant, approved));
  };

  const handleSearch = (page: number, pageSize: number): void => {
    searchDistributionLists(tenant, page, pageSize);
  };

  const handleDismiss = () => {
    setIsSidePanelOpen(false);
    setSelectedJoinRequest(undefined);
  };

  const onRowClick = (dlJoinRequest: IDistributionListsJoinRequest): void => {
    setSelectedJoinRequest(dlJoinRequest);
    setIsSidePanelOpen(true);
  };

  const isLoadingDlJoinRequest = networkOperationStatus === OperationStatus.Pending;
  const isApprovingDlJoinRequest = networkOperationStatusApprove == OperationStatus.Pending;

  useEffect(() => {
    if (isLoadingDlJoinRequest) return;

    if (tenant !== currentTenant) {
      resetDistributionLists();
      handleSearch(1, defaultListingPageSize);
      return;
    }

    handleSearch(currentPage, pageSize);
  }, [currentPage, pageSize, tenant]);

  useEffect(() => {
    if (networkOperationStatusApprove === OperationStatus.Succeeded) {
      handleSearch(currentPage, pageSize);
      setSelectedJoinRequest(undefined);
      setIsSidePanelOpen(false);
    }
  }, [networkOperationStatusApprove]);

  const { hasPermission } = useAuth();
  const canUpdateDistributionList =
    (tenant.toLowerCase() === TenantTypesEnum.MVP.toLowerCase() &&
      hasPermission(TenantTypesEnum.MVP, PermissionsEnum.MVP_UPDATE_DISTRIBUTIONS_LIST)) ||
    (tenant.toLowerCase() === TenantTypesEnum.RD.toLowerCase() &&
      hasPermission(TenantTypesEnum.RD, PermissionsEnum.RD_UPDATE_BENEFITS));

  return (
    <Stack horizontal styles={containerStackStyles}>
      <Stack.Item grow styles={listStackStyles()}>
        <Stack styles={tableStackStyles}>
          <PaddedFlexRow>
            <Title>Pending Request</Title>
            <ListCounts total={filteredCount} loading={isLoadingDlJoinRequest} />
          </PaddedFlexRow>
          <DistributionListPendingRequestsList
            data={distributionLists}
            filteredCount={filteredCount}
            pageSize={pageSize}
            isLoadingDlJoinRequest={isLoadingDlJoinRequest}
            currentPage={currentPage}
            loadDlJoinRequestError={networkOperationStatusError}
            actions={{
              onRowClick,
              updatePageSize,
              updateCurrentPage,
              approveOrDeclineJoinRequest: (dlJoinRequestId: number, approved: boolean): void => {
                approveOrDeclineJoinRequest(dlJoinRequestId, tenant, approved);
              },
            }}
          />
          <DistributionListsJoinRequestForm
            isOpen={isSidePanelOpen}
            dlJoinRequest={selectedJoinRequest}
            disableActions={isApprovingDlJoinRequest || !canUpdateDistributionList}
            error={networkOperationStatusApproveError}
            onSave={(): void => {
              if (!!selectedJoinRequest) {
                approveOrDeclineJoinRequest(selectedJoinRequest.requestId, tenant, true);
              }
            }}
            onCancel={(): void => {
              if (!!selectedJoinRequest) {
                approveOrDeclineJoinRequest(selectedJoinRequest.requestId, tenant, false);
              }
            }}
            onDismiss={handleDismiss}
          />
        </Stack>
      </Stack.Item>
    </Stack>
  );
};

export default DistributionListRequestsContainer;
